import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  filterAndSortProjects,
  formatCurrencyCents,
  type ProjectSortKey,
  type ProjectTableRow,
} from "@/lib/sponsor"

const ALL_TAGS = "__all__"

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  invited: "bg-amber-100 text-amber-800",
  transferring: "bg-blue-100 text-blue-800",
  exited: "bg-gray-100 text-gray-600",
}

function SortHeader({
  label,
  sortKey,
  current,
  ascending,
  onSort,
  className,
}: {
  label: string
  sortKey: ProjectSortKey
  current: ProjectSortKey
  ascending: boolean
  onSort: (key: ProjectSortKey) => void
  className?: string
}) {
  const active = current === sortKey
  const Icon = active ? (ascending ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-gray-900"
      >
        {label}
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </th>
  )
}

export function SponsorProjectTable({ rows }: { rows: ProjectTableRow[] }) {
  const [search, setSearch] = useState("")
  const [tag, setTag] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<ProjectSortKey>("ytd")
  const [ascending, setAscending] = useState(false)

  const allTags = useMemo(() => {
    const tags = new Set<string>()
    for (const r of rows) for (const t of r.sponsorship.tags) tags.add(t)
    return [...tags].sort()
  }, [rows])

  const visible = useMemo(
    () => filterAndSortProjects(rows, search, tag, sortKey, ascending),
    [rows, search, tag, sortKey, ascending]
  )

  const handleSort = (key: ProjectSortKey) => {
    if (key === sortKey) {
      setAscending((a) => !a)
    } else {
      setSortKey(key)
      setAscending(key === "name" || key === "status")
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects..."
            className="pl-8"
            aria-label="Search projects"
          />
        </div>
        {allTags.length > 0 && (
          <Select
            value={tag ?? ALL_TAGS}
            onValueChange={(v) => setTag(v === ALL_TAGS ? null : v)}
          >
            <SelectTrigger className="w-40" aria-label="Filter by tag">
              <SelectValue placeholder="All tags" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TAGS}>All tags</SelectItem>
              {allTags.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader label="Project" sortKey="name" current={sortKey} ascending={ascending} onSort={handleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Mode</th>
              <SortHeader label="Status" sortKey="status" current={sortKey} ascending={ascending} onSort={handleSort} />
              <SortHeader label="Raised YTD" sortKey="ytd" current={sortKey} ascending={ascending} onSort={handleSort} />
              <SortHeader label="Last donation" sortKey="lastDonation" current={sortKey} ascending={ascending} onSort={handleSort} />
              <SortHeader label="Members" sortKey="members" current={sortKey} ascending={ascending} onSort={handleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Fund balance</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Tags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((row) => (
              <tr key={row.sponsorship.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link
                    to={`/sponsor/orgs/${row.sponsorship.child_org_id}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {row.sponsorship.child.name}
                  </Link>
                  {(row.pendingRequests ?? 0) > 0 && (
                    <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                      {row.pendingRequests} request{row.pendingRequests === 1 ? "" : "s"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={row.sponsorship.mode === "fiscal" ? "default" : "secondary"}>
                    {row.sponsorship.mode}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[row.sponsorship.status] ?? "bg-gray-100 text-gray-600"}`}
                  >
                    {row.sponsorship.status}
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums">{formatCurrencyCents(row.ytdCents)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {row.lastDonationAt
                    ? new Date(row.lastDonationAt).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-4 py-3 tabular-nums">{row.memberCount}</td>
                <td className="px-4 py-3 tabular-nums">
                  {row.fundBalanceCents != null ? formatCurrencyCents(row.fundBalanceCents) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {row.sponsorship.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                  No projects match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
